/**
 * Someone else saved this item while you were editing it.
 *
 * WHY THIS STORE EXISTS AT ALL. The backend can now refuse a write built on a
 * stale read (409, `kind: "edit_conflict"`). Shipping that refusal WITHOUT a
 * place to render it would be worse than the bug it fixes: today you silently
 * lose your edit, and the alternative would be a raw error that reads like the
 * product is broken rather than like a colleague got there first.
 *
 * So the backend guard and this surface ship together. Modelled on
 * scopeBlock/creditBlock, which already prove the shape: a payload type-guard, a
 * tiny store, and one branch in the API client.
 */
import { create } from "zustand";

export interface EditConflictPayload {
  kind: "edit_conflict";
  entity_type: string;
  entity_id: string;
  your_version: number;
  current_version: number;
  message: string;
  /** The edit the server refused, so the dialog can hand it back verbatim. */
  your_changes?: Record<string, unknown>;
}

/**
 * Narrow an unknown 409 body. Deliberately strict: a 409 that is NOT an edit
 * conflict (a duplicate-key clash, say) must fall through to the generic error
 * path rather than be mis-rendered as "someone else edited this".
 */
export function isEditConflictPayload(v: unknown): v is EditConflictPayload {
  if (typeof v !== "object" || v === null) return false;
  const p = v as Record<string, unknown>;
  return (
    p.kind === "edit_conflict" &&
    typeof p.message === "string" &&
    typeof p.entity_id === "string" &&
    typeof p.your_version === "number" &&
    typeof p.current_version === "number"
  );
}

interface EditConflictState {
  conflict: EditConflictPayload | null;
  show: (p: EditConflictPayload) => void;
  dismiss: () => void;
}

export const useEditConflictStore = create<EditConflictState>((set) => ({
  conflict: null,
  show: (p) => set({ conflict: p }),
  dismiss: () => set({ conflict: null }),
}));
